/**
 * Monitored Carriers List Component
 *
 * Displays user's carrier watchlist with quick stats and unread alert counts.
 */

import React, { useEffect, useState } from 'react';
import { Truck, Bell, Shield, TrendingUp, Trash2, Eye } from 'lucide-react';
import { LoadingSpinner } from './shared/LoadingSpinner';
import { monitoringApi } from '../services/api';
import { logger } from '../utils/logger';
import '../styles/modern.css';

interface MonitoredCarrier {
  id: number;
  dotNumber: number;
  legalName: string;
  dbaName: string | null;
  statusCode: string;
  safetyRating: string | null;
  allowedToOperate: string;
  totalDrivers: number;
  totalPowerUnits: number;
  addedAt: string;
  lastUpdated: string | null;
  unreadAlerts: number;
}

interface MonitoredCarriersListProps {
  onCarrierClick?: (dotNumber: number) => void;
  onRemove?: (dotNumber: number) => void;
}

export const MonitoredCarriersList: React.FC<MonitoredCarriersListProps> = ({
  onCarrierClick,
  onRemove,
}) => {
  const [carriers, setCarriers] = useState<MonitoredCarrier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchMonitoredCarriers();
  }, []);

  const fetchMonitoredCarriers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use centralized API service
      const result = await monitoringApi.getMonitoredCarriers();
      setCarriers((result as { data?: MonitoredCarrier[] }).data || (result as MonitoredCarrier[]) || []);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load monitored carriers';
      logger.error('Error fetching monitored carriers:', { error: err });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (dotNumber: number, carrierName: string) => {
    if (!confirm(`Remove ${carrierName} from your watchlist?`)) {
      return;
    }

    try {
      // Use centralized API service
      await monitoringApi.removeCarrierFromWatchlist(dotNumber);

      // Update local state
      setCarriers(carriers.filter(c => c.dotNumber !== dotNumber));

      if (onRemove) {
        onRemove(dotNumber);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove carrier from monitoring';
      logger.error('Error removing carrier:', { error: err, dotNumber });
    }
  };

  const getSafetyRatingBadge = (rating: string | null) => {
    if (!rating) {
      return <span className="badge-modern badge-neutral">Not Rated</span>;
    }

    const badges: Record<string, { class: string; text: string }> = {
      'S': { class: 'badge-success', text: 'Satisfactory' },
      'C': { class: 'badge-warning', text: 'Conditional' },
      'U': { class: 'badge-danger', text: 'Unsatisfactory' },
    };

    const badge = badges[rating] || { class: 'badge-neutral', text: rating };

    return <span className={`badge-modern ${badge.class}`}>{badge.text}</span>;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
        <LoadingSpinner message="Loading carriers..." size="md" />
        <p style={{ marginTop: 'var(--space-md)', color: 'var(--color-gray-500)' }}>
          Loading monitored carriers...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-modern" style={{ borderLeft: '4px solid var(--color-danger)' }}>
        <p style={{ color: 'var(--color-danger)' }}>Error: {error}</p>
        <button onClick={fetchMonitoredCarriers} className="btn-primary" style={{ marginTop: 'var(--space-md)' }}>
          Retry
        </button>
      </div>
    );
  }

  if (carriers.length === 0) {
    return (
      <div className="card-modern" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
        <Truck size={48} style={{ color: 'var(--color-gray-300)', margin: '0 auto var(--space-lg)' }} />
        <h3 className="heading-3" style={{ color: 'var(--color-gray-500)' }}>
          No Monitored Carriers
        </h3>
        <p className="text-muted" style={{ marginTop: 'var(--space-sm)' }}>
          Search for a carrier and add them to your watchlist to start monitoring.
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
      {carriers.map((carrier, idx) => (
        <div
          key={carrier.id}
          className="card-modern fade-in-up"
          style={{ animationDelay: `${idx * 0.05}s` }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-md)' }}>
            {/* Carrier Info */}
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
                <h3 className="heading-3" style={{ margin: 0 }}>
                  {carrier.legalName}
                </h3>
                {carrier.unreadAlerts > 0 && (
                  <span className="badge-modern badge-danger" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Bell size={12} />
                    {carrier.unreadAlerts}
                  </span>
                )}
              </div>

              {carrier.dbaName && (
                <p className="text-muted" style={{ fontSize: '0.875rem', marginBottom: 'var(--space-sm)' }}>
                  DBA: {carrier.dbaName}
                </p>
              )}

              <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', marginBottom: 'var(--space-md)' }}>
                <code className="badge-modern badge-info" style={{ fontFamily: 'monospace' }}>
                  DOT {carrier.dotNumber}
                </code>
                {getSafetyRatingBadge(carrier.safetyRating)}
                <span className={`badge-modern ${carrier.statusCode === 'A' ? 'badge-success' : 'badge-danger'}`}>
                  {carrier.statusCode === 'A' ? 'Active' : 'Inactive'}
                </span>
                <span className={`badge-modern ${carrier.allowedToOperate === 'Y' ? 'badge-success' : 'badge-danger'}`}>
                  {carrier.allowedToOperate === 'Y' ? 'Allowed to Operate' : 'Not Allowed'}
                </span>
              </div>

              {/* Fleet Stats */}
              <div style={{ display: 'flex', gap: 'var(--space-xl)', fontSize: '0.8125rem', color: 'var(--color-gray-600)' }}>
                <span>{carrier.totalDrivers} Drivers</span>
                <span>{carrier.totalPowerUnits} Power Units</span>
                <span className="text-muted">Added: {new Date(carrier.addedAt).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
              <button
                onClick={() => onCarrierClick && onCarrierClick(carrier.dotNumber)}
                className="btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', padding: '0.5rem 0.75rem' }}
              >
                <Eye size={16} />
                View
              </button>
              <button
                onClick={() => handleRemove(carrier.dotNumber, carrier.legalName)}
                className="btn-secondary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-xs)',
                  padding: '0.5rem 0.75rem',
                  color: 'var(--color-danger)',
                }}
              >
                <Trash2 size={16} />
                Remove
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
